import { BehaviorSubject, catchError, filter, map, Observable, Subscription, takeWhile, tap } from 'rxjs';
import { ChangeTyped, Dto, dtoConverter, ID_FIELD_OPTIONS, isDefined, Query, SearchResult } from '@damofire/data-commons';
import { collection, collectionData, doc, docData, DocumentData, Firestore, query } from '@angular/fire/firestore';
import { ObservableQueryService } from './services';
import { QueryConstraint } from '@firebase/firestore';
import { isEqual } from 'lodash-es';
import { Auth, User } from '@angular/fire/auth';

export class FirebaseObservableService<T extends Dto> {
  private listeningForLogout = false;
  private loggedOut = false;
  private sub: Subscription;
  private previousPreFilter: QueryConstraint[];
  private previousCollection: string;
  private readonly data: BehaviorSubject<T[]> = new BehaviorSubject<T[]>(null);
  private logoutUnsub: (() => void) | null = null;

  static mergeChanges<T extends Dto>(newSearchResult: T[], previousSearchResult: ChangeTyped<T>[] = []): ChangeTyped<T>[] {
    const changes: ChangeTyped<T>[] = [];
    newSearchResult.forEach((change: T) => {
      const prevFound = previousSearchResult.find((prev: ChangeTyped<T>) => prev._id === change._id);
      if (prevFound) {
        if (change.audit.lastModifiedDate !== prevFound.audit.lastModifiedDate) {
          changes.push({ ...change, changeType: 'modified' });
        }
      } else {
        changes.push({ ...change, changeType: 'added' });
      }
    });

    previousSearchResult.forEach((prev: ChangeTyped<T>) => {
      if (!newSearchResult.find((change: T) => prev._id === change._id)) {
        changes.push({ ...prev, changeType: 'removed' });
      }
    });

    return changes;
  }

  static toChangeType<T extends Dto>(newDoc: T, previousDoc: ChangeTyped<T>): ChangeTyped<T> {
    let converted: ChangeTyped<T>;

    if (!newDoc?.audit) {
      converted = { _id: previousDoc._id, changeType: 'removed' } as ChangeTyped<T>;
    } else if (previousDoc && newDoc.audit.lastModifiedDate !== previousDoc.audit.lastModifiedDate) {
      converted = { ...(newDoc as T), changeType: 'modified' };
    } else {
      converted = { ...(newDoc as T), changeType: 'added' };
    }

    return converted;
  }

  constructor(
    protected readonly firestoreQueryService: ObservableQueryService,
    protected readonly collectionName: string,
    protected readonly firestore: Firestore,
    protected readonly auth: Auth,
  ) {}

  observeDoc(id: string): Observable<ChangeTyped<T>> {
    this.listenForLogout();

    let previousDoc: T;
    let stop = false;
    return docData(doc(this.firestore, `${this.collectionName}/${id}`).withConverter(dtoConverter()), ID_FIELD_OPTIONS).pipe(
      catchError((err) => {
        console.error(`catchError on ${this.collectionName}/${id}`, err, stop);
        throw err;
      }),
      map<DocumentData, ChangeTyped<T>>((fromFirestore) => FirebaseObservableService.toChangeType(fromFirestore as T, previousDoc)),
      tap((newDoc) => {
        previousDoc = newDoc;
        if (newDoc.changeType === 'removed') {
          stop = true;
        }
      }),
      takeWhile(() => !(stop || this.loggedOut), true),
    );
  }

  observeCollectionSearch(sQuery: Query, searchFields: string[], preFilter: QueryConstraint[] = [], collectionNameOverride?: string): Observable<SearchResult<T>> {
    return this.observeCollection(preFilter, collectionNameOverride).pipe(map((data) => this.firestoreQueryService.search<T>(data, sQuery, searchFields)));
  }

  observeCollection(preFilter: QueryConstraint[], collectionNameOverride?: string): Observable<T[]> {
    const collectionName = collectionNameOverride || this.collectionName;
    this.listenForLogout();
    if (!isEqual(preFilter, this.previousPreFilter) || this.previousCollection !== collectionName) {
      this.sub?.unsubscribe();
      this.previousPreFilter = preFilter;
      this.previousCollection = collectionName;
      this.data.next(null);

      console.log(`Listening on ${collectionName}`, preFilter);
      this.sub = collectionData<T>(query(collection(this.firestore, collectionName).withConverter(dtoConverter<T>()), ...preFilter), ID_FIELD_OPTIONS)
        .pipe(takeWhile(() => !this.loggedOut))
        .subscribe((d) => {
          this.data.next(d as T[]);
        });
    }
    return this.data.asObservable().pipe(filter(isDefined));
  }

  /**
   * Returns an isolated observable for the collection query.
   *
   * This does not share any state or filters with the service, so each call is fully independent.
   * Useful for cases where shared state causes unwanted side effects or lag.
   */
  observeCollectionIsolated(preFilter: QueryConstraint[], collectionNameOverride?: string): Observable<T[]> {
    const collectionName = collectionNameOverride || this.collectionName;
    this.listenForLogout();
    return new Observable<T[]>((subscriber) => {
      const sub = collectionData<T>(query(collection(this.firestore, collectionName).withConverter(dtoConverter<T>()), ...preFilter), ID_FIELD_OPTIONS).subscribe({
        next: (d) => subscriber.next(d as T[]),
        error: (err) => subscriber.error(err),
        complete: () => subscriber.complete(),
      });
      return () => sub.unsubscribe();
    })
      .pipe(takeWhile(() => !this.loggedOut))
      .pipe(filter(isDefined));
  }

  collection<T>(path: string, q: Query, searchFields?: string[]): Promise<SearchResult<T>> {
    return this.firestoreQueryService.collection(path, q, searchFields);
  }

  search<T>(dataToFilter: T[], q: Query, searchFields?: string[]): SearchResult<T> {
    return this.firestoreQueryService.search(dataToFilter, q, searchFields);
  }

  private listenForLogout(): void {
    if (!this.listeningForLogout) {
      this.listeningForLogout = true;
      this.loggedOut = false;
      this.logoutUnsub = this.auth.onAuthStateChanged((user: User) => {
        if (!user) {
          this.sub?.unsubscribe();
          this.loggedOut = true;
          this.logoutUnsub?.();
          this.logoutUnsub = null;
          this.listeningForLogout = false;
        } else {
          this.loggedOut = false;
        }
      });
    }
  }
}
